import type React from 'react';
import {useEffect, useState} from 'react';
import {isAxiosError} from 'axios';
import {Alert, Box, Chip, Divider, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {queryHttp} from '~/query/queryHttp';
import {riskPreviewUrl} from '../services/categoryProfileUrls';
import type {RiskPreview, SettingValue} from '../types/settingsTypes';

const WEIGHTS = ['risk_weight_concentration', 'risk_weight_coverage', 'risk_weight_volatility', 'risk_weight_signals'];
const LEVELS: {key: keyof RiskPreview['levels']; label: string; color: 'error' | 'warning' | 'success' | 'default'}[] = [
    {key: 'high', label: 'High', color: 'error'}, {key: 'medium', label: 'Medium', color: 'warning'},
    {key: 'low', label: 'Low', color: 'success'}, {key: 'insufficient', label: 'Too little data', color: 'default'},
];

const PREVIEW_DELAY_MS = 600;

/** The server's per-setting errors, or why the preview is not available (too many asks, not allowed to try changes). */
function previewErrorText(e: unknown): string {
    const status = isAxiosError(e) ? e.response?.status : undefined;
    if (status === 429) return 'Too many previews in a short time. Wait a moment, then change a setting again.';
    if (status === 403) return 'You need edit access to try other settings.';
    const errors = isAxiosError(e) ? (e.response?.data as {errors?: Record<string, string>} | undefined)?.errors : undefined;
    return errors ? Object.values(errors).join(' ') : 'Could not preview the risk levels.';
}

type Props = {
    databagId: number;
    /** The group's values as edited (saved values with the unsaved changes on top). */
    values: Record<string, SettingValue>;
    /** Only the unsaved changes: the preview scores with these. */
    changes: Record<string, SettingValue>;
};

/**
 * Risk scoring, under its settings: the weight total, what this dataset offers each factor, and how many categories
 * would be high, medium and low (and on how much data) with the settings as edited, before saving.
 */
export const RiskScoringPanel: React.FC<Props> = ({databagId, values, changes}) => {
    const [preview, setPreview] = useState<RiskPreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const total = WEIGHTS.reduce((s, k) => s + Number(values[k] ?? 0), 0);
    const key = JSON.stringify(changes);

    useEffect(() => {
        if (total !== 100) return undefined;
        // Wait for the typing to stop before asking; an answer to an earlier question is dropped
        let stale = false;
        const timer = setTimeout(() => {
            queryHttp.post<RiskPreview>(riskPreviewUrl(databagId), {changes})
                .then(r => {
                    if (stale) return;
                    setPreview(r.data);
                    setError(null);
                })
                .catch(e => {
                    if (!stale) setError(previewErrorText(e));
                });
        }, PREVIEW_DELAY_MS);
        return () => {
            stale = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [databagId, key, total]);

    return (
        <Stack gap={1.5} sx={{mt: 2}}>
            <Divider/>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">Weights</Typography>
                <Chip size="small" color={total === 100 ? 'success' : 'error'} variant="outlined"
                      label={total === 100 ? 'Add up to 100%' : `Add up to ${total}%: they must add up to 100%`}/>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {!preview && !error && <Skeleton variant="rounded" height={160}/>}
            {preview && (
                <>
                    <Box>
                        <Typography variant="subtitle2">
                            {Object.keys(changes).length ? 'With these changes, this dataset would have' : 'This dataset has'}
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap" sx={{mt: 0.5}}>
                            {LEVELS.map(l => (
                                <Chip key={l.key} size="small" color={l.color} variant={l.key === 'insufficient' ? 'outlined' : 'filled'}
                                      label={`${l.label}: ${preview.levels[l.key]}`}/>
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                            {`Of the ranked categories: ${preview.confidence.full} on full data, ${preview.confidence.partial} on partial `
                                + `data, ${preview.confidence.limited} on limited data.`}
                        </Typography>
                    </Box>
                    <Box sx={{overflowX: 'auto'}}>
                        <Table size="small" aria-label="What the dataset offers each factor">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Factor</TableCell>
                                    <TableCell>Data in this dataset</TableCell>
                                    <TableCell align="right">Counts for</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preview.availability.map(a => (
                                    <TableRow key={a.key} sx={a.available ? undefined : {'& td': {color: 'text.secondary'}}}>
                                        <TableCell sx={{fontWeight: a.available ? 600 : 400, whiteSpace: 'nowrap'}}>{a.label}</TableCell>
                                        <TableCell>{a.available ? a.detail : `${a.detail}. Not used now; its weight goes to the others.`}</TableCell>
                                        <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>{`${a.usedBy} of ${a.ranked} categories`}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </>
            )}
        </Stack>
    );
};
